import { hasAFeatureFlag } from "helpers/featureHelper";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { setUserFeatureFlags } from "newui/features/Auth/authSlice";
import type { FeatureFlag } from "newui/features/Auth/types/FeatureFlag.type";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";
import type { RootState } from "newui/store";
import { useAppSelector } from "newui/store/hooks";

import { useCurrentUser } from "./auth";

export const useCanAccessOnboarding = (): boolean => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_ONBOARDING);
};

export const useCurrentUserHasDashboard = () => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_DASHBOARD);
};

export const useCurrentUserHasRFxPreApproval = () => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_PRE_APPROVAL_FLOW);
};

export const useCurrentUserHasFeatureFlag = (
	featureFlag: FeatureFlagType
): boolean => {
	return hasAFeatureFlag(useCurrentUserFeatureFlags(), featureFlag);
};

export const useCurrentUserFeatureFlags = (): FeatureFlagType[] => {
	const { currentUser } = useCurrentUser();
	const dispatch = useDispatch();
	const existingFeatures = useAppSelector(
		(state: RootState) => state?.auth?.user_feature_flags
	) as FeatureFlagType[] | undefined;

	// Memoize mapping of currentUser.feature_flags -> FeatureFlagType[]
	const currentUserFeatureTypes = useMemo(() => {
		return (
			currentUser?.feature_flags?.map(
				(feature: FeatureFlag) => feature.id as FeatureFlagType
			) || []
		);
	}, [currentUser?.feature_flags]);

	// Dispatch to Redux only if we have features and Redux doesn't yet
	useEffect(() => {
		if (
			currentUserFeatureTypes.length > 0 &&
			(!existingFeatures || existingFeatures.length === 0)
		) {
			dispatch(setUserFeatureFlags(currentUserFeatureTypes));
		}
	}, [currentUserFeatureTypes, existingFeatures, dispatch]);

	// Return either Redux state or fallback to currentUser features
	return existingFeatures && existingFeatures.length > 0
		? existingFeatures
		: currentUserFeatureTypes;
};
